import { defineStore } from "pinia";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { sureClone } from "@/lib/DataUtils";
import { createStore } from "./storeHelper";
import _ from "lodash";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:AnnouncementsStore`);

const DEFAULT_EDITOR = {
  id: null,
  name: null,
  description: null,
  categoryId: null,
  isActive: true,
};

export const useAnnouncementsStore = defineStore(
  "announcements",
  createStore({
    state: {
      announcements: {},
      editor: sureClone(DEFAULT_EDITOR),
      announcementApiError: null,
    },

    getters: {
      announcementsList: (state) => _.values(state.announcements),
      announcementsByCategoryId: (state) => (categoryId) => {
        return _.values(state.announcements).filter(
          (a) => a.announcementCategoryId === categoryId,
        );
      },
      editorChanges() {
        return this.getEditorChanges("announcements");
      },
      editorHasChanges() {
        const diff = this.editorChanges;
        return diff && !_.isEmpty(diff);
      },
    },

    actions: {
      newAnnouncementToEditor() {
        this.set("editor", sureClone(DEFAULT_EDITOR));
      },

      addAnnouncement(announcement) {
        this.announcements[announcement.id] = announcement;
      },

      setAnnouncementApiError(error) {
        this.announcementApiError = error;
      },

      clearAnnouncements() {
        this.announcements = {};
      },

      deleteAnnouncementFromState(announcementId) {
        delete this.announcements[announcementId];
      },

      async load() {
        debug("load");
        try {
          const announcements = await ApiClient.get(`/announcements`);
          debug("load", announcements);
          this.set("announcements", _.keyBy(announcements, "id"));
          this.setAnnouncementApiError(null);
        } catch (error) {
          debug("load error", error);
          this.setAnnouncementApiError(error);
          throw error;
        }
      },

      async loadAnnouncementToEditor(announcementId) {
        debug("loadAnnouncementToEditor", announcementId);
        const announcement = this.announcements[announcementId];
        if (!announcement) {
          throw new Error("Announcement not found");
        }
        this.set("editor", sureClone(announcement));
      },

      async createAnnouncement() {
        debug("createAnnouncement", this.editor);
        const result = await ApiClient.post(`/announcements`, this.editor);
        debug("createAnnouncement result", result);

        if (result) {
          this.addAnnouncement(result);
        }
        return result;
      },

      async saveAnnouncementInEditor() {
        if (!this.editorHasChanges) {
          debug("saveAnnouncementInEditor", "No changes to save");
          return;
        }

        const announcementId = this.editor.id;
        debug("saveAnnouncementInEditor", announcementId, this.editor);
        const result = await ApiClient.put(
          `/announcements/${announcementId}`,
          this.editor,
        );
        debug("saveAnnouncementInEditor result", result);

        if (result) {
          this.addAnnouncement(result);
        }
        return result;
      },

      async deleteAnnouncement(announcementId) {
        debug("deleteAnnouncement", announcementId);
        await ApiClient.delete(`/announcements/${announcementId}`);
        this.deleteAnnouncementFromState(announcementId);
      },
    },
  }),
);
